<template>
  <div class="tool-page">
    <div class="tool-header">
      <h1>{{ $t('listSum.title') }}</h1>
      <p>{{ $t('listSum.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="form-group">
        <label>{{ $t('listSum.inputLabel') }}</label>
        <textarea
          v-model="input"
          :placeholder="$t('listSum.inputPlaceholder')"
          rows="10"
          class="input-area"
          @input="calculate"
        ></textarea>
      </div>

      <div class="options">
        <label class="option-label">
          {{ $t('listSum.separatorLabel') }}
          <select v-model="separator" @change="calculate" class="sep-select">
            <option value="line">{{ $t('listSum.sepLine') }}</option>
            <option value="comma">{{ $t('listSum.sepComma') }}</option>
            <option value="space">{{ $t('listSum.sepSpace') }}</option>
            <option value="tab">{{ $t('listSum.sepTab') }}</option>
          </select>
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="ignoreNonNumber" @change="calculate" />
          {{ $t('listSum.ignoreNonNumber') }}
        </label>
        <label class="option-label">
          <input type="checkbox" v-model="decimalMode" @change="calculate" />
          {{ $t('listSum.decimalMode') }}
        </label>
      </div>

      <div class="results-panel" v-if="stats.count > 0">
        <div class="stats-grid">
          <div class="stat-card primary">
            <span class="stat-label">{{ $t('listSum.sum') }}</span>
            <span class="stat-value">{{ formatNum(stats.sum) }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t('listSum.average') }}</span>
            <span class="stat-value">{{ formatNum(stats.avg) }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t('listSum.count') }}</span>
            <span class="stat-value">{{ stats.count }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t('listSum.max') }}</span>
            <span class="stat-value">{{ formatNum(stats.max) }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t('listSum.min') }}</span>
            <span class="stat-value">{{ formatNum(stats.min) }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">{{ $t('listSum.median') }}</span>
            <span class="stat-value">{{ formatNum(stats.median) }}</span>
          </div>
        </div>

        <div class="number-list" v-if="parsedNumbers.length > 0 && parsedNumbers.length <= 100">
          <h3>{{ $t('listSum.parsedList') }}</h3>
          <div class="number-tags">
            <span class="num-tag" v-for="(n, i) in parsedNumbers" :key="i">{{ formatNum(n) }}</span>
          </div>
        </div>

        <div class="skipped-info" v-if="skippedItems.length > 0">
          <p>⚠️ {{ $t('listSum.skippedInfo').replace('{n}', skippedItems.length) }}</p>
        </div>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('listSum.whatIs') }}</h2>
      <p>{{ $t('listSum.whatIsDesc') }}</p>
      <h3>{{ $t('listSum.tipsTitle') }}</h3>
      <ul>
        <li v-for="(tip, i) in $t('listSum.tips')" :key="i">{{ tip }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ListSum',
  data() {
    return {
      input: '',
      separator: 'line',
      ignoreNonNumber: true,
      decimalMode: true,
      parsedNumbers: [],
      skippedItems: [],
      stats: { sum: 0, avg: 0, count: 0, max: 0, min: 0, median: 0 }
    }
  },
  methods: {
    calculate() {
      if (!this.input.trim()) {
        this.parsedNumbers = []
        this.skippedItems = []
        this.stats = { sum: 0, avg: 0, count: 0, max: 0, min: 0, median: 0 }
        return
      }

      let sepPattern
      switch (this.separator) {
        case 'line': sepPattern = /\n/; break
        case 'comma': sepPattern = /[,，]/; break
        case 'space': sepPattern = /\s+/; break
        case 'tab': sepPattern = /\t/; break
        default: sepPattern = /\n/
      }

      const parts = this.input.split(sepPattern)
      const numbers = []
      const skipped = []

      for (const part of parts) {
        const trimmed = part.trim()
        if (!trimmed) continue

        // 提取数字，支持千分位格式如 1,234.56
        const cleaned = trimmed.replace(/,/g, '')
        const num = Number(cleaned)

        if (!isNaN(num) && cleaned !== '') {
          numbers.push(num)
        } else if (this.ignoreNonNumber) {
          skipped.push(trimmed)
        }
      }

      this.parsedNumbers = numbers
      this.skippedItems = skipped

      if (numbers.length === 0) {
        this.stats = { sum: 0, avg: 0, count: 0, max: 0, min: 0, median: 0 }
        return
      }

      const sum = numbers.reduce((a, b) => a + b, 0)
      const sorted = [...numbers].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      const median = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2

      this.stats = {
        sum,
        avg: sum / numbers.length,
        count: numbers.length,
        max: Math.max(...numbers),
        min: Math.min(...numbers),
        median
      }
    },
    formatNum(n) {
      if (this.decimalMode) {
        // 保留合理的小数位
        const str = n.toFixed(6)
        return parseFloat(str).toLocaleString('zh-CN', { maximumFractionDigits: 6 })
      }
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 800px; margin: 0 auto; }
.tool-header { text-align: center; margin-bottom: 32px; }
.tool-header h1 { font-size: 28px; color: var(--text-primary); margin-bottom: 8px; }
.tool-header p { color: var(--text-secondary); font-size: 16px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 32px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }

.form-group { margin-bottom: 16px; }
.form-group label { display: block; font-size: 14px; font-weight: 600; color: var(--text-primary); margin-bottom: 8px; }
.input-area { width: 100%; padding: 12px 16px; border: 1px solid var(--border-color); border-radius: 10px; background: var(--bg-input); color: var(--text-primary); font-size: 14px; resize: vertical; box-sizing: border-box; outline: none; font-family: 'Courier New', monospace; }
.input-area:focus { border-color: var(--accent-color); }

.options { display: flex; gap: 16px; flex-wrap: wrap; align-items: center; margin-bottom: 20px; }
.option-label { display: flex; align-items: center; gap: 6px; font-size: 14px; color: var(--text-secondary); cursor: pointer; }
.sep-select { background: var(--bg-input); color: var(--text-primary); border: 1px solid var(--border-color); border-radius: 6px; padding: 4px 8px; font-size: 13px; cursor: pointer; outline: none; }

.stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 20px; }
.stat-card { background: var(--bg-tertiary); border-radius: 12px; padding: 16px; display: flex; flex-direction: column; gap: 4px; text-align: center; }
.stat-card.primary { background: var(--accent-light); }
.stat-label { font-size: 13px; color: var(--text-tertiary); }
.stat-value { font-size: 22px; font-weight: 700; color: var(--text-primary); font-family: 'Courier New', monospace; }
.stat-card.primary .stat-value { color: var(--accent-color); }

.number-list h3 { font-size: 14px; color: var(--text-primary); margin-bottom: 8px; }
.number-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.num-tag { padding: 4px 10px; background: var(--bg-tertiary); border-radius: 6px; font-size: 13px; color: var(--text-secondary); font-family: 'Courier New', monospace; }

.skipped-info { margin-top: 12px; }
.skipped-info p { font-size: 13px; color: var(--text-tertiary); margin: 0; }

.info-card h2 { font-size: 20px; color: var(--text-primary); margin-bottom: 12px; }
.info-card h3 { font-size: 16px; color: var(--text-primary); margin: 16px 0 8px; }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; }
.info-card ul { padding-left: 20px; margin: 0; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 2; }

@media (max-width: 768px) {
  .stats-grid { grid-template-columns: repeat(2, 1fr); }
  .options { flex-direction: column; align-items: flex-start; }
}
</style>
